import React, { useCallback, useEffect, useRef, useState } from 'react'
import mux from 'mux-embed'
import { useLocation, useNavigate } from 'react-router'

import tts from '../../lib/TTS.js'
import {
  getComcastErrorTypeFromPlayerError,
  getTTSDuration,
} from '../../../../shared/helpers.js'
import Spinner from '../Spinner/SpinnerTS'
import Platform from '../../platforms/index.js'
import Player, {
  PLAYER_REPORTABLE_EVENTS,
} from '../../../../shared/components/VideoPlayer/index.js'
import { buildMezzanineUrl, buildMuxData } from './utils'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { useWatchProgress } from './hooks/useWatchProgress'
import PlayerControls from './PlayerControls/PlayerControls.tsx'
import { useVideoPlayerData } from './VideoPlayerContext'

import type { VideoPlayerProps, PlayerEventData } from './types'
import type { VideoPlayer } from '../../../../shared/components/VideoPlayer/types'
import AdContainer from './adContainer/AdContainer.tsx'
import { useUpNext } from './hooks/useGetUpNext.ts'
import UpNext from '../UpNext/UpNext.tsx'
import useMediaEvent, { VOD } from './useMediaEvent.ts'
import dayjs from 'dayjs'
import ContinueWatchingModal from '../ContinueWatchingModal/ContinueWatchingModal.tsx'
import { USER_INACTIVITY_TIMEOUT_MS } from '../ContinueWatchingModal/types.ts'

const DRM = 'drm'
const NO_DRM = 'no_drm'
const PLAYER_DOM_ID = 'video-player-id'
const WIDTH = 1920
const HEIGHT = 1080
const PLAYER_ID = Math.floor(Math.random() * 9000000000) + 1000000000

function VideoPlayer(props: VideoPlayerProps) {
  const dispatch = useAppDispatch()
  const device = useAppSelector((state) => state.device)
  const deviceHasAdsEnabled = useAppSelector((state) => state.device.adsEnabled)
  const videoDetails = useAppSelector((state) => state.content.videoDetails)
  const { userPid } = useAppSelector((state) => ({
    userPid: state.user.pid,
  }))
  const navigate = useNavigate()
  const playerType = videoDetails.drmWidevideLicenseUrl ? DRM : NO_DRM
  const mezzanine = buildMezzanineUrl(videoDetails.assetMezzanine)
  const playerRef = useRef<VideoPlayer | null>(null)
  const [captionsAvailable, setCaptionsAvailable] = useState(false)
  const adsEnabled =
    deviceHasAdsEnabled &&
    videoDetails.duration > 60 * 3 &&
    playerType === NO_DRM
  const location = useLocation()
  const [isRebuffering, setIsRebuffering] = useState(false)
  const {
    playerState,
    playerStateRef,
    setPlayerStatus,
    setCurrentProgress,
    setDuration,
  } = useVideoPlayerData()
  const showSpinner = isRebuffering || playerState.status === 'init'
  const { sendMediaStartEvent } = useMediaEvent(VOD)
  const isAdPlaying =
    playerState.status === 'adPlaying' || playerState.status === 'adPaused'
  const [sourceDimentions, setSourceDimensions] = useState({
    sourceHeight: 0,
    sourceWidth: 0,
  })
  const playerElementRef = useRef<HTMLVideoElement>(null)
  const emitMuxEvent = useCallback(
    (eventName: string, eventData?: any) => {
      mux.emit(PLAYER_ID, eventName, eventData)
    },
    [PLAYER_ID]
  )

  const [showContinueWatchingModal, setShowContinueWatchingModal] =
    useState(false)
  useUpNext()
  useWatchProgress(videoDetails.videoId, () => playerState.currentProgress)

  useEffect(() => {
    tts.clearPushedText()
    tts.stopRead()
    setCurrentProgress(props.initialPlayheadPosition)
    setDuration(videoDetails.duration)
    dispatch({ type: 'UNSET_UPNEXT_DATA' })
    const playerProps = {
      contentSourceUrl: !adsEnabled ? videoDetails.url : null,
      reportPlayerEvent,
      setCaptionsAvailable: setCaptionsAvailable,
      drmWidevideLicenseUrl: videoDetails.drmWidevideLicenseUrl,
    }
    const newPlayer =
      playerType === NO_DRM
        ? Platform.createVODPlayer(playerElementRef, playerProps)
        : Platform.createVODDRMPlayer(playerElementRef, playerProps)

    playerRef.current = newPlayer
    mux.init(PLAYER_ID, {
      debug: false,
      beaconCollectionDomain: 'qoe.pbs.org',
      getPlayheadTime: () => playerState.currentProgress * 1000,
      getStateData: () => {
        return {
          player_is_paused:
            playerState.status === 'paused' ||
            playerState.status === 'adPaused',
          video_source_height: sourceDimentions.sourceHeight,
          video_source_width: sourceDimentions.sourceWidth,
          video_source_url: videoDetails.url,
          video_source_mime_type: playerType === DRM ? 'dash' : 'hls',
          video_source_duration: videoDetails.duration * 1000,
          video_poster_url: mezzanine,
          player_autoplay_on: true,
          player_preload_on: false,
          player_is_fullscreen: true,
          player_width: WIDTH,
          player_height: HEIGHT,
          player_language_code: 'en-US',
        }
      },
      data: buildMuxData(
        videoDetails,
        userPid,
        device.deviceModel,
        playerRef.current?.getVersion() || ''
      ),
    })
    emitMuxEvent('viewstart')
    newPlayer?.init()

    return () => {
      emitMuxEvent('viewend')
      emitMuxEvent('destroy')
      playerRef.current?.destroy()
    }
  }, [])

  const handleFatalError = ({ code, data }) => {
    dispatch({
      type: 'TRIGGER_ERROR',
      payload: {
        error: {
          status: code,
          code: data.type,
          data,
          content: {
            errorPath: location.pathname,
          },
          codeBaseLocation: 'VideoPlayer - handleFatalError',
          errorType: getComcastErrorTypeFromPlayerError(code),
        },
      },
    })
    navigate('/error', { replace: true })
  }

  const reportPlayerEvent = (
    eventName: string,
    eventData: PlayerEventData = {}
  ): void => {
    const { bitrate, width, height, code, message, data } = eventData
    switch (eventName) {
      case PLAYER_REPORTABLE_EVENTS.RENDITION_CHANGE:
        setSourceDimensions({
          sourceHeight: height || HEIGHT,
          sourceWidth: width || WIDTH,
        })
        emitMuxEvent('renditionchange', {
          video_source_bitrate: bitrate,
          video_source_width: width,
          video_source_height: height,
        })
        break
      case PLAYER_REPORTABLE_EVENTS.REBUFFER_START:
        emitMuxEvent('rebufferstart')
        setIsRebuffering(true)
        break
      case PLAYER_REPORTABLE_EVENTS.BUFFER_STALLED:
        //seek(this.state.playheadPosition + 0.5)
        break
      case PLAYER_REPORTABLE_EVENTS.ERROR:
        emitMuxEvent('error', {
          player_error_code: code,
          player_error_message: message,
          player_error_context: data,
        })
        handleFatalError({ code, data })
        break
      default:
        break
    }
  }

  const handlePlaying = () => {
    emitMuxEvent('rebufferend')
    setPlayerStatus('playing')
    emitMuxEvent('playing')
    sendMediaStartEvent()
    setIsRebuffering(false)
    Platform.startPlayback()
  }

  const handlePaused = () => {
    setPlayerStatus('paused')
    emitMuxEvent('pause')
  }

  const checkEnd = (currentTime: number) => {
    return (
      videoDetails.duration > 0 &&
      (currentTime > videoDetails.duration ||
        Math.abs(videoDetails.duration - currentTime) < 0.3)
    )
  }

  const handleTimeUpdate = (currentTime: number) => {
    if (checkEnd(currentTime) && playerStateRef.current.status !== 'ended') {
      handleEnded()
    }
    emitMuxEvent('timeupdate', {
      player_playhead_time: currentTime * 1000,
    })
    setCurrentProgress(currentTime)
  }

  const handleEnded = () => {
    setPlayerStatus('ended')
    emitMuxEvent('ended')
    exitPlayback()
  }

  const exitPlayback = () => {
    const { upNextData, lastUserInteraction } = playerStateRef.current
    const upNextVisible = upNextData !== null && upNextData.isUpNextVisible

    if (upNextVisible) {
      const inactiveDuration = dayjs().diff(lastUserInteraction, 'milliseconds')

      if (inactiveDuration >= USER_INACTIVITY_TIMEOUT_MS) {
        setShowContinueWatchingModal(true)
      } else {
        navigate(
          `/video-${
            upNextData?.isMvod && !upNextData?.isPlayable
              ? 'details'
              : 'playback'
          }/${upNextData?.upNextUrl}`,
          { replace: true, state: { fromUpNext: true } }
        )
      }
    } else {
      Platform.endPlayback()
      tts.pushTextToRead('Exiting video playback.')
      const windowUrl = location.pathname.split('/')[2]
      navigate(`/video-details/${windowUrl}`, { replace: true })
    }
  }

  const playOrPause = () => {
    switch (playerState.status) {
      case 'playing':
        tts.addTextToRead(
          `Paused at ${getTTSDuration(playerState.currentProgress)}`
        )
        playerRef.current?.pause()
        break
      case 'paused':
        tts.addTextToRead(
          `Playing from ${getTTSDuration(playerState.currentProgress)}`
        )
        playerRef.current?.play()
        break
      case 'adPlaying':
        tts.addTextToRead(
          `Paused at ${getTTSDuration(playerState.adData?.adCurrentTime)}`
        )
        setPlayerStatus('adPaused')
        break
      case 'adPaused':
        tts.addTextToRead(
          `Playing from ${getTTSDuration(playerState.adData?.adCurrentTime)}`
        )
        setPlayerStatus('adPlaying')
        break
      default:
        break
    }
  }

  const backToPlayingContent = () => {
    setPlayerStatus('init')
    setIsRebuffering(true)
    setDuration(videoDetails.duration)
    setCurrentProgress(props.initialPlayheadPosition)
    if (videoDetails.url) {
      playerRef.current?.load(videoDetails.url)
    }
    playerRef.current?.play()
  }

  const handleSeek = (seekTime: number) => {
    setPlayerStatus('seeked')
    setCurrentProgress(seekTime)
    playerRef.current?.seek(seekTime)
    emitMuxEvent('seeked')
    playerRef.current?.play()
  }

  const handleLoadedData = () => {
    if (props.initialPlayheadPosition > 0) {
      playerRef.current?.seek(props.initialPlayheadPosition)
    }
  }

  const shouldEnableUpNext =
    playerState.upNextData !== null &&
    !isAdPlaying &&
    playerState.status !== 'init'

  const closeContinueWatchingModal = () => {
    setShowContinueWatchingModal(false)
  }

  const handleContinueWatchingNavigation = (continueWatching: boolean) => {
    closeContinueWatchingModal()
    if (continueWatching) {
      navigateToUpNextContent()
    } else {
      navigateToVideoDetails()
    }
  }

  const navigateToVideoDetails = () => {
    const windowUrl = window.location.href.split('/')[4]
    navigate(`/video-details/${windowUrl}`, { replace: true })
  }

  const navigateToUpNextContent = () => {
    const upNextData = playerStateRef.current.upNextData
    const pathType =
      upNextData?.isMvod && !upNextData?.isPlayable ? 'details' : 'playback'
    navigate(`/video-${pathType}/${upNextData?.upNextUrl}`, {
      replace: true,
      state: {
        fromContinueWatchingModal: true,
      },
    })
  }

  return (
    <div id="video-player-container" aria-hidden={true}>
      {showContinueWatchingModal ? (
        <ContinueWatchingModal
          handleContinueWatchingNavigation={handleContinueWatchingNavigation}
        />
      ) : (
        <>
          <div
            className="video-player-mezzanine"
            style={{
              backgroundImage: mezzanine,
              visibility:
                playerState.status === 'init' || playerState.status === 'loaded'
                  ? 'visible'
                  : 'hidden',
            }}
          />
          {showSpinner && !isAdPlaying && <Spinner />}
          <PlayerControls
            captionsAvailable={captionsAvailable && !isAdPlaying}
            playOrPause={playOrPause}
            seek={handleSeek}
            pause={() => playerRef.current?.pause()}
            play={() => playerRef.current?.play()}
          />
          <Player
            ref={playerElementRef}
            onLoadedData={handleLoadedData}
            onPlaying={handlePlaying}
            onPaused={handlePaused}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onError={(e: Error) => {
              console.error('Error', e)
            }}
            captionsAvailable={captionsAvailable}
            domID={PLAYER_DOM_ID}
          />
          {adsEnabled && (
            <AdContainer
              playerRef={playerElementRef}
              pause={() => playerRef.current?.pause()}
              emitMuxEvent={emitMuxEvent}
              backToPlayingContent={backToPlayingContent}
            />
          )}
          {shouldEnableUpNext && <UpNext />}
        </>
      )}
    </div>
  )
}

export default VideoPlayer
